import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, X, AlertCircle, ArrowUpFromLine, ShieldAlert } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { formatApiError } from '../../lib/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mobile-money operators supported by Rafiki (matches backend models/rafiki.py).
const RAFIKI_OPERATORS = [
  'SAFARICOM', 'AT', 'AIRTEL', 'VODACOM', 'TIGO', 'HALOTEL', 'TTCL', 'MTN',
  'VODAFONE', 'MOOV', 'ORANGE', 'FREE', 'EXPRESSO', 'WAVE', 'BKASH', 'NAGAD',
  'GCASH', 'MAYA', 'COINS', 'GRABPAY', 'EASYPAISA', 'JAZZCASH', 'UPAISA',
  'SADAPAY', 'NAYAPAY', 'FINJA', 'PAYMAX',
];

const inputCls = 'w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500';

// Rafiki payout modal — reuses POST /rafiki/payouts + the shared ledger.
const RafikiPaymentModal = ({ onClose, onDone }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    environment: 'sandbox', type: 'MOBILE_MONEY',
    amount: '100', currency: 'KES', country: 'KE',
    holder_name: '', holder_type: 'INDIVIDUAL',
    phone_number: '+254700000000', operator: 'SAFARICOM',
    bank_id: '', account_number: '', purpose: 'REMITTANCE',
  });
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadBanks = useCallback(async () => {
    if (banks.length || banksLoading) return;
    setBanksLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/rafiki/banks`);
      setBanks(res.data?.banks || []);
    } catch (e) { setBanks([]); } finally { setBanksLoading(false); }
  }, [banks.length, banksLoading]);

  const onTypeChange = (t) => { set('type', t); setError(''); if (t === 'BANK_ACCOUNT') loadBanks(); };

  const validate = () => {
    const amt = parseFloat(form.amount);
    if (!form.amount || isNaN(amt) || amt <= 0) return 'Amount must be a positive number.';
    if (!/^[A-Za-z]{3}$/.test(form.currency.trim())) return 'Currency must be a 3-letter code (e.g. KES).';
    if (!/^[A-Za-z]{2}$/.test(form.country.trim())) return 'Country must be a 2-letter code (e.g. KE).';
    if (!form.holder_name.trim()) return 'Recipient name is required.';
    if (form.type === 'BANK_ACCOUNT') {
      if (!form.bank_id) return 'Select a bank.';
      if (!form.account_number.trim()) return 'Account number is required.';
    } else {
      if (!/^\+\d{8,15}$/.test(form.phone_number.trim())) return 'Phone number must be E.164 (e.g. +254700000000).';
      if (!RAFIKI_OPERATORS.includes(form.operator)) return 'Select a valid mobile-money operator.';
    }
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true);
    try {
      const body = {
        environment: form.environment, type: form.type,
        amount: form.amount.trim(), currency: form.currency.trim().toUpperCase(),
        country: form.country.trim().toUpperCase(), holder_name: form.holder_name.trim(),
      };
      if (form.type === 'BANK_ACCOUNT') {
        body.holder_type = form.holder_type;
        body.bank_id = form.bank_id;
        body.account_number = form.account_number.trim();
      } else {
        body.phone_number = form.phone_number.trim();
        body.operator = form.operator;
      }
      if (form.purpose) body.purpose = form.purpose;
      const res = await axios.post(`${API_URL}/api/admin/payments/rafiki/payouts`, body);
      toast({ title: 'Rafiki payout initiated', description: `Status: ${res.data.status}` });
      onDone();
    } catch (err) {
      setError(formatApiError(err, 'Failed to send payout.'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="rafiki-payout-modal">
        <div className="flex items-center justify-between p-5 border-b border-slate-700 sticky top-0 bg-slate-800">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ArrowUpFromLine className="w-5 h-5 text-amber-400" /> New Rafiki Payout
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4" data-testid="rafiki-payout-modal-form">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="rafiki-payout-modal-error">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Environment</label>
              <select data-testid="rafiki-payout-modal-environment" value={form.environment}
                onChange={(e) => { set('environment', e.target.value); setError(''); }} className={inputCls}>
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Payout Type</label>
              <select data-testid="rafiki-payout-modal-type" value={form.type}
                onChange={(e) => onTypeChange(e.target.value)} className={inputCls}>
                <option value="MOBILE_MONEY">Mobile Money</option>
                <option value="BANK_ACCOUNT">Bank Account</option>
              </select>
            </div>
          </div>

          {form.environment === 'production' && (
            <div data-testid="rafiki-payout-modal-prod-warning" className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs">
              <ShieldAlert className="w-4 h-4" /> Production sends real money. Requires Owner to enable Production payouts.
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Amount</label>
              <input data-testid="rafiki-payout-modal-amount" type="text" value={form.amount}
                onChange={(e) => set('amount', e.target.value)} placeholder="100" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Currency</label>
              <input data-testid="rafiki-payout-modal-currency" type="text" value={form.currency}
                onChange={(e) => set('currency', e.target.value)} placeholder="KES" className={`${inputCls} uppercase`} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Country</label>
              <input data-testid="rafiki-payout-modal-country" type="text" value={form.country}
                onChange={(e) => set('country', e.target.value)} placeholder="KE" className={`${inputCls} uppercase`} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Recipient Name</label>
              <input data-testid="rafiki-payout-modal-holder" type="text" value={form.holder_name}
                onChange={(e) => set('holder_name', e.target.value)} placeholder="John Doe" className={inputCls} />
            </div>

            {form.type === 'MOBILE_MONEY' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Operator</label>
                  <select data-testid="rafiki-payout-modal-operator" value={form.operator}
                    onChange={(e) => set('operator', e.target.value)} className={inputCls}>
                    {RAFIKI_OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Phone (E.164)</label>
                  <input data-testid="rafiki-payout-modal-phone" type="text" value={form.phone_number}
                    onChange={(e) => set('phone_number', e.target.value)} placeholder="+254700000000" className={`${inputCls} font-mono`} />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Holder Type</label>
                  <select data-testid="rafiki-payout-modal-holder-type" value={form.holder_type}
                    onChange={(e) => set('holder_type', e.target.value)} className={inputCls}>
                    <option value="INDIVIDUAL">Individual</option>
                    <option value="BUSINESS">Business</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Bank</label>
                  <select data-testid="rafiki-payout-modal-bank" value={form.bank_id}
                    onChange={(e) => set('bank_id', e.target.value)} className={inputCls} disabled={banksLoading}>
                    <option value="">{banksLoading ? 'Loading banks…' : 'Select a bank'}</option>
                    {banks.map((b) => <option key={b.id} value={b.id}>{b.name}{b.country ? ` (${b.country})` : ''}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Account Number</label>
                  <input data-testid="rafiki-payout-modal-account-number" type="text" value={form.account_number}
                    onChange={(e) => set('account_number', e.target.value)} placeholder="12345678" className={`${inputCls} font-mono`} />
                </div>
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Purpose <span className="text-slate-500 font-normal">(optional)</span></label>
            <select data-testid="rafiki-payout-modal-purpose" value={form.purpose}
              onChange={(e) => set('purpose', e.target.value)} className={inputCls}>
              <option value="">—</option>
              {['REMITTANCE','P2P_TRANSFER','SALARY_AND_WAGES','SERVICES_PAYMENT','GOODS_PURCHASE','BILLS_PAYMENT','INVOICE_PAYMENT','DONATION'].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white transition-colors">Cancel</button>
            <button type="submit" disabled={saving} data-testid="rafiki-payout-modal-submit-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit Payout</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RafikiPaymentModal;
